import { useMemo } from 'react';

import {
  MeasurementRangesRequestParams,
  MeasurementSeriesStatusMap,
  useGetMeasurementRanges,
} from '@restDataAccess';
import { SortDirection } from '@scaleaq/sitecontrol-lib';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import {
  AggregatedChartConfigType,
  ChartAggregationResolution,
  ChartType,
  getChartNameWithPeriod,
  useChartTimeRange,
} from '@/components';
import { toApiDate, toApiDateTime } from '@/utils';

import { getChartMeasurements } from './chartMeasurements.functions.ts';

// Shared default, so an omitted `normalizeTimeSeries` keeps its identity across renders and the
// measurements memo below does not rebuild every series on each render.
const NO_NORMALIZED_SERIES: string[] = [];

type useHistoricalDataQueriesProps = {
  config: AggregatedChartConfigType;
  normalizeTimeSeries?: string[];
};

export const useDataFromConfig = ({
  config,
  normalizeTimeSeries = NO_NORMALIZED_SERIES,
}: useHistoricalDataQueriesProps) => {
  const { t } = useTranslation(['chartConfig']);
  const { plot, sensors, type, interval, from, to, resolution, windowSize } = config;

  const plotAsAStringArray = useMemo(() => plot.map(p => p.value), [plot]);
  const sensorAsStringArray = useMemo(() => sensors.map(s => s.value), [sensors]);

  // Live aggregated queries leave `end` open towards now (see `queries` below), so day-snapping
  // only aligns `start` with the daily buckets and keeps the request stable until midnight.
  // Consumers that send `end` to the API must not snap, or today's data would be cut off.
  const { start, end, updateInterval } = useChartTimeRange(
    type,
    interval,
    {
      start: from ?? DateTime.now(),
      end: to ?? DateTime.now(),
    },
    resolution === ChartAggregationResolution.DAILY
  );

  const isLive = useMemo(() => type === ChartType.LIVE, [type]);
  const useApiDates = resolution === ChartAggregationResolution.DAILY;

  const chartName = useMemo(() => {
    const baseName = (sensors.length > 1 ? plot[0]?.label : sensors[0]?.label) || undefined;
    return getChartNameWithPeriod({
      baseName,
      isLive,
      interval,
      from,
      to,
      t,
    });
  }, [from, interval, isLive, plot, sensors, t, to]);

  const queries = useMemo<MeasurementRangesRequestParams[]>(() => {
    const serializedStart = serializeRangeBound(start ?? DateTime.now(), useApiDates);
    const serializedEnd = !isLive && end ? serializeRangeBound(end, useApiDates) : undefined;

    return sensorAsStringArray.flatMap(inventoryId =>
      plotAsAStringArray.map(plot => ({
        inventoryId,
        type: plot,
        start: serializedStart,
        end: serializedEnd,
        sortDirection: SortDirection.Ascending,
        resolution: resolution,
        includeSubSensors: true,
        mergeSeries: true,
        windowSize: windowSize,
      }))
    );
  }, [
    sensorAsStringArray,
    plotAsAStringArray,
    start,
    isLive,
    end,
    resolution,
    windowSize,
    useApiDates,
  ]);

  const { data, isLoading, isError, isFetched, isRateLimited, seriesStatus } =
    useGetMeasurementRanges(queries, updateInterval.toMillis());

  const { allMeasurements, isTimeSeriesNormalized } = useMemo(
    () => getChartMeasurements({ data, normalizeTimeSeries, resolution }),
    [data, normalizeTimeSeries, resolution]
  );

  return {
    chartName,
    isLive,
    isTimeSeriesNormalized,
    plot,
    sensors,
    data: {
      allMeasurements: allMeasurements || {},
      isLoading,
      isError,
      isFetched,
      isRateLimited,
      // Per plot-type / sensor; use for row-level UI. Aggregates above stay chart-oriented.
      seriesStatus: seriesStatus ?? ({} as MeasurementSeriesStatusMap),
    },
  };
};

const serializeRangeBound = (value: DateTime, useApiDates: boolean): string =>
  useApiDates ? toApiDate(value) : toApiDateTime(value);
